// hooks/useAppointmentBooking.js
import { useState, useEffect } from 'react';
import { db } from '../LoginComp/firebase'; 
import { 
  collection, 
  query, 
  where, 
  getDocs,
  getDoc,
  addDoc,
  doc, 
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import moment from 'moment';
import emailjs from 'emailjs-com';
import { MdAccessTime, MdEvent, MdVolumeUp, MdClose, MdPerson, 
         MdPhone, MdEmail, MdLocationOn, MdPeople, MdNotes, 
         MdCheckCircle } from 'react-icons/md';

const useAppointmentBooking = () => {
  // Common state for both booking types
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    numberOfPeople: 1,
    location: '',
    specialRequests: ''
  });
  const [showForm, setShowForm] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeTab, setActiveTab] = useState('alms');

  // Alms Giving specific state
  const [selectedMealType, setSelectedMealType] = useState('');
  const [bookedDates, setBookedDates] = useState({ breakfast: [], lunch: [] });

  // Dhamma Speech/Pirith Chanting specific state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bookedEvents, setBookedEvents] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);



  //error message 
  const [bookingError, setBookingError] = useState('');



  // Monastery Donation state
  const [formDonateData, setFormDonateData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    date: '',
    amount: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define available time slots for events
  const eventTypes = {
    'Pirith Chanting / Dhamma Speech': [
      { time: '8:00 AM - 10:00 AM', id: 'morning' },
      { time: '3:00 PM - 5:00 PM', id: 'afternoon' },
      { time: '7:00 PM - 9:00 PM', id: 'evening' }
    ]
  };





  // Fetch already booked dates for Alms Giving

  useEffect(() => {
    const fetchBookedDates = async () => {
      try {
        const breakfastQuery = query(collection(db, 'appointments'), where('mealType', '==', 'breakfast'));
        const lunchQuery = query(collection(db, 'appointments'), where('mealType', '==', 'lunch'));
        
        const [breakfastSnapshot, lunchSnapshot] = await Promise.all([
          getDocs(breakfastQuery),
          getDocs(lunchQuery)
        ]);

        const breakfastDates = breakfastSnapshot.docs.map(doc => {
          const data = doc.data();
          return data.date?.toDate() || null;
        }).filter(date => date !== null);

        const lunchDates = lunchSnapshot.docs.map(doc => {
          const data = doc.data();
          return data.date?.toDate() || null;
        }).filter(date => date !== null);

        setBookedDates({
          breakfast: breakfastDates,
          lunch: lunchDates
        });
      } catch (error) {
        console.error("Error fetching booked dates:", error);
        setBookedDates({ breakfast: [], lunch: [] });
      }
    };

    if (activeTab === 'alms') {
      fetchBookedDates();
    }
  }, [activeTab]);


 


  // Fetch already booked events for Dhamma Speech/Pirith Chanting
  useEffect(() => {
    const fetchBookedEvents = async () => {
      try {
        const eventsQuery = query(collection(db, 'dhammaEvents'));
        const snapshot = await getDocs(eventsQuery);
        
        const events = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate() || null,
            timeSlot: data.timeSlot || ''
          };
        }).filter(event => event.date !== null);

        setBookedEvents(events);
      } catch (error) {
        console.error("Error fetching booked events:", error);
        setBookedEvents([]);
      }
    };

    if (activeTab === 'dhamma') {
      fetchBookedEvents();
    }
  }, [activeTab]);

  // Generate time slots for selected date (Dhamma Speech/Pirith Chanting)
  useEffect(() => {
    if (selectedDate && activeTab === 'dhamma') {
      const today = new Date();
      const isToday = moment(selectedDate).isSame(today, 'day');
      
      const slots = eventTypes['Pirith Chanting / Dhamma Speech'].map(slot => {
        const [startTime, endTime] = slot.time.split(' - ');
        const slotStart = moment(selectedDate).set({
          hour: parseInt(startTime.split(':')[0]) + (startTime.includes('PM') && parseInt(startTime.split(':')[0]) !== 12 ? 12 : 0),
          minute: parseInt(startTime.split(':')[1].split(' ')[0]),
          second: 0
        }).toDate();
        
        const isPast = isToday && new Date() > slotStart;
        const isBooked = bookedEvents.some(event => 
          moment(event.date).isSame(selectedDate, 'day') && 
          event.timeSlot === slot.id
        );

        return {
          ...slot,
          isPast,
          isBooked,
          startTime: slotStart
        };
      });

      setTimeSlots(slots);
    }
  }, [selectedDate, bookedEvents, activeTab]);

  // Common handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const navigateMonth = (months) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + months);
    setCurrentMonth(newMonth);
  };

  // Alms Giving handlers
  const handleAlmsDateSelect = (date, mealType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      alert('You cannot select a past date');
      return;
    }

    const isBooked = bookedDates[mealType]?.some(bookedDate => 
      moment(bookedDate).isSame(date, 'day')
    ) || false;

    if (isBooked) {
      alert(`This date is already booked for ${mealType} Offering`);
      return;
    }

    setSelectedDate(date);
    setSelectedMealType(mealType);
    setShowForm(true);
  };

  const almsEventStyleGetter = (date, mealType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return {
        className: 'past-date',
        style: {
          cursor: 'not-allowed'
        }
      };
    }

    const isBooked = bookedDates[mealType]?.some(bookedDate => 
      moment(bookedDate).isSame(date, 'day')
    ) || false;

    if (isBooked) {
      return {
        className: 'booked-date',
        style: {
          cursor: 'not-allowed'
        }
      };
    }

    return {
      className: `available-date ${mealType}`,
      style: {
        cursor: 'pointer'
      }
    };
  };

  // Dhamma Speech/Pirith Chanting handlers
  const handleDhammaDateSelect = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      alert('You cannot select a past date');
      return;
    }

    setSelectedDate(date);
  };

  const handleTimeSlotSelect = (slot) => {
    if (slot.isPast) {
      alert('This time slot has already passed');
      return;
    }

    if (slot.isBooked) {
      alert('This time slot is already booked');
      return;
    }

    setSelectedEvent(slot);
    setShowForm(true);
  };

  const dhammaEventStyleGetter = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return {
        className: 'past-date',
        style: {
          cursor: 'not-allowed'
        }
      };
    }

    const hasBookings = bookedEvents?.some(event => 
      moment(event.date).isSame(date, 'day')
    ) || false;

    return {
      className: hasBookings ? 'has-bookings' : 'available-date',
      style: {
        cursor: 'pointer'
      }
    };
  };


const handleSubmit = async (e) => {
  e.preventDefault();

  if (isSubmitting) return;
  setIsSubmitting(true);

  try {
    if (!formData.name || !formData.email || !formData.phone) {
      throw new Error('Please fill all required fields');
    }

    const bookingDate = selectedDate;
    const dateKey = moment(bookingDate).format('YYYY-MM-DD');

    let collectionName = '';
    let slotKey = '';

    if (activeTab === 'alms') {
      collectionName = 'appointments';
      slotKey = `${dateKey}_${selectedMealType}`;
    } else {
      collectionName = 'dhammaEvents';
      slotKey = `${dateKey}_${selectedEvent.id}`;
    }

    const docRef = doc(db, collectionName, slotKey);

    // 🔒 HARD LOCK CHECK
    const existing = await getDoc(docRef);
    if (existing.exists()) {
      throw new Error('This date and time is already booked');
    }

    const bookingData = {
      ...formData,
      date: bookingDate,
      slotKey,
      type: activeTab,
      status: 'confirmed',
      ...(activeTab === 'alms' && {
        mealType: selectedMealType,
        timeSlot: selectedMealType === 'breakfast' ? 'morning' : 'afternoon'
      }),
      ...(activeTab !== 'alms' && {
        eventType: 'Pirith Chanting / Dhamma Speech',
        timeSlot: selectedEvent.id,
        timeSlotDisplay: selectedEvent.time
      }),
      createdAt: serverTimestamp()
    };

    // ✅ ATOMIC SAVE
    await setDoc(docRef, bookingData);

    // UI updates
    if (activeTab === 'alms') {
      setBookedDates(prev => ({
        ...prev,
        [selectedMealType]: [...prev[selectedMealType], bookingDate]
      }));
    } else {
      setBookedEvents(prev => [...prev, bookingData]);
    }

    setSubmissionSuccess(true);
    setShowForm(false);

  } catch (error) {
  console.error(error);

  if (error.message === 'This date and time is already booked') {
    setBookingError(
      '❌ Sorry, this slot was just booked by someone else. Please choose another date or time.'
    );

    // Close form & reset selection
    setShowForm(false);
    setSelectedEvent(null);

    // Force refresh availability
    if (activeTab === 'alms') {
      setActiveTab('');
      setTimeout(() => setActiveTab('alms'), 0);
    } else {
      setActiveTab('');
      setTimeout(() => setActiveTab('dhamma'), 0);
    }
  } else {
    alert('Something went wrong. Please try again.');
  }
} finally {
  setIsSubmitting(false);
}

};



  // Form submission

// const handleSubmit = async (e) => {               commented on 17th of february 2026
//   e.preventDefault();
  
//   if (isSubmitting) return; // Prevent multiple submissions
  
//   setIsSubmitting(true);
  
//   try {
//     if (!formData.name || !formData.email || !formData.phone) {
//       throw new Error('Please fill all required fields');
//     }

//     const bookingDate = selectedDate || new Date();
//     const bookingData = {
//       ...formData,
//       date: bookingDate,
//       type: activeTab,
//       status: 'pending',
//       ...(activeTab === 'alms' && { 
//         mealType: selectedMealType || 'lunch',
//         timeSlot: selectedMealType === 'breakfast' ? 'morning' : 'afternoon'
//       }),
//       ...(activeTab !== 'alms' && { 
//         eventType: 'Pirith Chanting / Dhamma Speech',
//         timeSlot: selectedEvent?.id || 'default',
//         timeSlotDisplay: selectedEvent?.time || 'To be determined'
//       }),
//       createdAt: new Date(),
//       updatedAt: new Date()
//     };

//     // Save to Firestore
//     let docRef;
//     try {
//       const collectionName = activeTab === 'alms' ? 'appointments' : 'dhammaEvents';
//       docRef = await addDoc(collection(db, collectionName), bookingData);
//       console.log('Firestore document written with ID: ', docRef.id);
      
//       // Update local state immediately after successful Firestore save
//       if (activeTab === 'alms') {
//         setBookedDates(prev => ({
//           ...prev,
//           [selectedMealType]: [...prev[selectedMealType], bookingDate]
//         }));
//       } else {
//         setBookedEvents(prev => [
//           ...prev,
//           {
//             date: bookingDate,
//             timeSlot: selectedEvent?.id || 'default',
//             timeSlotDisplay: selectedEvent?.time || 'To be determined'
//           }
//         ]);
//       }
//     } catch (firebaseError) {
//       console.error('Firebase save failed:', firebaseError);
//       throw new Error('Failed to save booking. Please try again.');
//     }

//     // Send Email to Admin
//     try {
//       await emailjs.send('service_598u6ac', 'template_soq22lx', {
//         name: formData.name,
//         email: formData.email,
//         phone: formData.phone,
//         address: formData.address,
//         number_of_people: formData.numberOfPeople,
//         location: formData.location,
//         special_requests: formData.specialRequests,
//         booking_type: activeTab === 'alms' 
//           ? `Alms Giving (${selectedMealType})` 
//           : `Dhamma Event (${selectedEvent?.time || 'Unknown time'})`,
//         booking_date: bookingDate.toDateString()
//       }, 'ecZ5RdpZWV8Tut7Ao');

//       console.log('Admin notification email sent successfully');
//     } catch (emailError) {
//       console.error('Email sending failed:', emailError);
//     }

//     setSubmissionSuccess(true);
//     setShowForm(false);
    
//     setTimeout(() => {
//       setFormData({
//         name: '',
//         phone: '',
//         email: '',
//         address: '',
//         numberOfPeople: 1,
//         location: '',
//         specialRequests: ''
//       });
//       setSubmissionSuccess(false);
//     }, 3000);

//   } catch (error) {
//     console.error('Booking submission error:', error);
//     alert(error.message || 'An unexpected error occurred. Please try again.');
//   } finally {
//     setIsSubmitting(false);
//   }
// };



  // const handleSubmit = async (e) => {
  //   e.preventDefault();
    
  //   try {
  //     if (!formData.name || !formData.email || !formData.phone) {
  //       throw new Error('Please fill all required fields');
  //     }
  
  //     const bookingDate = selectedDate || new Date();
  //     const bookingData = {
  //       ...formData,
  //       date: bookingDate,
  //       type: activeTab,
  //       status: 'pending',
  //       ...(activeTab === 'alms' && { 
  //         mealType: selectedMealType || 'lunch',
  //         timeSlot: selectedMealType === 'breakfast' ? 'morning' : 'afternoon'
  //       }),
  //       ...(activeTab !== 'alms' && { 
  //         eventType: 'Pirith Chanting / Dhamma Speech',
  //         timeSlot: selectedEvent?.id || 'default',
  //         timeSlotDisplay: selectedEvent?.time || 'To be determined'
  //       }),
  //       createdAt: new Date(),
  //       updatedAt: new Date()
  //     };
  
  //     // Save to Firestore
  //     let docRef;
  //     try {
  //       const collectionName = activeTab === 'alms' ? 'appointments' : 'dhammaEvents';
  //       docRef = await addDoc(collection(db, collectionName), bookingData);
  //       console.log('Firestore document written with ID: ', docRef.id);
        
  //       // Update local state immediately after successful Firestore save
  //       if (activeTab === 'alms') {
  //         setBookedDates(prev => ({
  //           ...prev,
  //           [selectedMealType]: [...prev[selectedMealType], bookingDate]
  //         }));
  //       } else {
  //         setBookedEvents(prev => [
  //           ...prev,
  //           {
  //             date: bookingDate,
  //             timeSlot: selectedEvent?.id || 'default',
  //             timeSlotDisplay: selectedEvent?.time || 'To be determined'
  //           }
  //         ]);
  //       }
  //     } catch (firebaseError) {
  //       console.error('Firebase save failed:', firebaseError);
  //       throw new Error('Failed to save booking. Please try again.');
  //     }
  
  //     // Send Email to Admin
  //     try {
  //       await emailjs.send('service_598u6ac', 'template_soq22lx', {
  //         name: formData.name,
  //         email: formData.email,
  //         phone: formData.phone,
  //         address: formData.address,
  //         number_of_people: formData.numberOfPeople,
  //         location: formData.location,
  //         special_requests: formData.specialRequests,
  //         booking_type: activeTab === 'alms' 
  //           ? `Alms Giving (${selectedMealType})` 
  //           : `Dhamma Event (${selectedEvent?.time || 'Unknown time'})`,
  //         booking_date: bookingDate.toDateString()
  //       }, 'ecZ5RdpZWV8Tut7Ao');
  
  //       console.log('Admin notification email sent successfully');
  //     } catch (emailError) {
  //       console.error('Email sending failed:', emailError);
  //     }
  
  //     setSubmissionSuccess(true);
  //     setShowForm(false);
  //     alert('Booking saved successfully!');
      
  //     setTimeout(() => {
  //       setFormData({
  //         name: '',
  //         phone: '',
  //         email: '',
  //         address: '',
  //         numberOfPeople: 1,
  //         location: '',
  //         specialRequests: ''
  //       });
  //       setSubmissionSuccess(false);
  //     }, 3000);
  
  //   } catch (error) {
  //     console.error('Booking submission error:', error);
  //     alert(error.message || 'An unexpected error occurred. Please try again.');
  //   }
  // };













  



  // Monastery Donation handlers
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormDonateData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  const handleDonateSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const docRef = await addDoc(collection(db, "monasteryDonations"), {
        ...formDonateData,
        currency: 'AUD',
        createdAt: serverTimestamp()
      });
      
      console.log("Document written with ID: ", docRef.id);
      alert('Thank you! Your donation details have been successfully submitted.');
      
      setFormDonateData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        date: '',
        amount: '',
        reason: ''
      });
    } catch (error) {
      console.error("Error adding document: ", error);
      alert('There was an error submitting your donation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom Toolbar Component
  const CustomToolbar = (toolbar) => {
    return (
      <div className="rbc-toolbar">
        <div className="month-navigation">
          <button 
            type="button" 
            onClick={() => navigateMonth(-1)}
            className="nav-button"
          >
            &lt;
          </button>
          <span className="rbc-toolbar-label">
            {toolbar.label}
          </span>
          <button 
            type="button" 
            onClick={() => navigateMonth(1)}
            className="nav-button"
          >
            &gt;
          </button>
        </div>
      </div>
    );
  };




  

  return {
    // State values
    selectedDate,
    formData,
    showForm,
    submissionSuccess,
    currentMonth,
    activeTab,
    bookedDates,
    bookedEvents,
    timeSlots,
    selectedMealType,
    selectedEvent,
    formDonateData,
    isSubmitting,
    
    // State setters (these were missing)
    setSelectedDate,
    setFormData,
    setShowForm,
    setSubmissionSuccess,
    setCurrentMonth,
    setActiveTab,
    setBookedDates,
    setBookedEvents,
    setTimeSlots,
    setSelectedMealType,
    setSelectedEvent,
    setFormDonateData,
    setIsSubmitting,
    
    // Handlers
    handleInputChange,
    navigateMonth,
    handleAlmsDateSelect,
    almsEventStyleGetter,
    handleDhammaDateSelect,
    handleTimeSlotSelect,
    dhammaEventStyleGetter,
    handleSubmit,
    handleChange,
    handleDonateSubmit,
    CustomToolbar






  };
};

export default useAppointmentBooking;